import { FC, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import { Ruler, RefreshCw, Search, ChevronDown, ChevronRight, Check, X, Pencil, Undo2 } from 'lucide-react';
import ContextStore from '../hooks/ContextStore';
import { urls } from '../dispatch/urls';
import { cn } from '../lib/utils';
import { Button } from '../components/ui/button';

// ─── Sizes ───────────────────────────────────────────────────────────────────────
// One product group per product (one name, one cost) — with a stock PER SIZE inside it. Once a group is counted
// size by size, every Jumia/Marjane SKU is pushed the stock of ITS size. Until then nothing changes. If the total
// changes without sizes (a delivery, an edit), the group goes back to one number and asks for a recount.
// Backend: controllers/sizes.js

interface Label { label: string; skus: number; stock: number | null }
interface Row {
  groupId: string; name: string; suggestedName: string | null; imageUrl: string | null; stock: number; reserved: number;
  sizes: { label: string; stock: number }[]; sizesCountedAt: string | null; status: string; labels: Label[]; sameProductGroups: string[];
}
interface Sku { id: string; sku: string; store: string; name: string; size: string | null; manual: boolean }
const errMsg = (e: any) => e?.response?.data?.error?.message || e?.message || 'Something went wrong';
const STATUS_TONE: Record<string, string> = { 'per size ✓': 'bg-emerald-100 text-emerald-800', 'recount sizes': 'bg-amber-100 text-amber-800', 'not counted per size': 'bg-slate-100 text-slate-600' };

const SizesPage: FC = () => {
  const { contextStore } = useContext(ContextStore);
  const api = useMemo(() => axios.create({ baseURL: `${urls.baseUrlRestApi}/sizes`, headers: { 'x-auth-token': contextStore?.token || '' } }), [contextStore?.token]);
  const [rows, setRows] = useState<Row[]>([]);
  const [loading, setLoading] = useState(false);
  const [flash, setFlash] = useState<{ ok: boolean; text: string } | null>(null);
  const [filter, setFilter] = useState<'all' | 'todo' | 'done' | 'recount'>('all');
  const [q, setQ] = useState('');
  const [open, setOpen] = useState<string | null>(null);          // count editor
  const [counts, setCounts] = useState<Record<string, string>>({});
  const [newSize, setNewSize] = useState('');
  const [skusOf, setSkusOf] = useState<string | null>(null);      // SKU list
  const [skus, setSkus] = useState<Sku[]>([]);
  const [busy, setBusy] = useState<string | null>(null);
  const say = (ok: boolean, text: string) => { setFlash({ ok, text }); window.setTimeout(() => setFlash(null), 7000); };

  const load = useCallback(async (detect = false) => {
    setLoading(true);
    try { setRows((await api.get('/', { params: detect ? { detect: 1 } : {} })).data); } catch (e) { say(false, errMsg(e)); }
    setLoading(false);
  }, [api]);
  useEffect(() => { load(); }, [load]);

  const shown = rows.filter((r) => (filter === 'all' || (filter === 'todo' && r.status === 'not counted per size') || (filter === 'done' && r.status === 'per size ✓') || (filter === 'recount' && r.status === 'recount sizes'))
    && (!q.trim() || r.name.toLowerCase().includes(q.trim().toLowerCase())));
  const n = (s: string) => rows.filter((r) => r.status === s).length;

  const openCount = (r: Row) => {
    setOpen(open === r.groupId ? null : r.groupId); setNewSize('');
    setCounts(Object.fromEntries(r.labels.map((l) => [l.label, l.stock != null ? String(l.stock) : ''])));
  };
  const saveCount = async (r: Row) => {
    const filled = Object.fromEntries(Object.entries(counts).filter(([, v]) => v !== '').map(([k, v]) => [k, Number(v)]));
    const total = Object.values(filled).reduce((a, b) => a + b, 0);
    if (!Object.keys(filled).length) return say(false, 'Enter the count of at least one size');
    if (total !== r.stock && !window.confirm(`The sizes add up to ${total}, the current stock is ${r.stock}.\n\nSave ${total} as the real stock of "${r.name}"?`)) return;
    setBusy(r.groupId);
    try { const x = (await api.post(`/${r.groupId}/count`, { counts: filled })).data; say(true, `${r.name}: counted per size — ${x.total} units (${x.sizes.map((s: any) => `${s.label} ${s.stock}`).join(' · ')}). Jumia & Marjane follow each size now.`); setOpen(null); await load(); }
    catch (e) { say(false, errMsg(e)); }
    setBusy(null);
  };
  const clear = async (r: Row) => {
    if (!window.confirm(`Go back to ONE stock number for "${r.name}"? The total (${r.stock}) stays; the sizes are forgotten.`)) return;
    try { await api.delete(`/${r.groupId}`); say(true, `${r.name}: back to one stock number`); await load(); } catch (e) { say(false, errMsg(e)); }
  };
  const rename = async (r: Row, name: string) => {
    if (!window.confirm(`Rename "${r.name}" to "${name}"?`)) return;
    try { await api.put(`/${r.groupId}/name`, { name }); say(true, `Renamed to ${name}`); await load(); } catch (e) { say(false, errMsg(e)); }
  };
  const showSkus = async (r: Row) => {
    if (skusOf === r.groupId) { setSkusOf(null); return; }
    setSkusOf(r.groupId); setSkus([]);
    try { setSkus((await api.get(`/${r.groupId}/skus`)).data); } catch (e) { say(false, errMsg(e)); }
  };
  const setSize = async (s: Sku, size: string) => {
    if ((s.size || '') === size.trim().toUpperCase()) return;
    try { const x = (await api.put(`/sku/${s.id}`, { size })).data; setSkus((list) => list.map((k) => k.id === s.id ? { ...k, size: x.size, manual: true } : k)); }
    catch (e) { say(false, errMsg(e)); }
  };

  return (
    <div className="space-y-4 p-4">
      <div className="flex flex-wrap items-end justify-between gap-2">
        <div>
          <h1 className="flex items-center gap-2 text-lg font-bold text-slate-800"><Ruler className="h-5 w-5 text-amber-500" /> Sizes</h1>
          <p className="max-w-3xl text-xs text-slate-500">One product group per product (one name, one cost) — with its stock <b>per size</b>. Count a group size by size once: from then on every Jumia / Marjane SKU shows the stock of its own size, and a size at 0 stops selling while the others go on. Until a group is counted per size nothing changes. If its total changes without sizes (a delivery, an edit), it goes back to one number and shows “recount sizes”.</p>
        </div>
        <Button variant="outline" size="sm" onClick={() => load(true)} disabled={loading}><RefreshCw className={cn('h-4 w-4', loading && 'animate-spin')} /> Detect sizes again</Button>
      </div>
      {flash && <p className={cn('rounded-lg border p-2.5 text-sm', flash.ok ? 'border-emerald-200 bg-emerald-50 text-emerald-800' : 'border-red-200 bg-red-50 text-red-700')}>{flash.text}</p>}

      <div className="flex flex-wrap items-center gap-2">
        {([['all', `All (${rows.length})`], ['todo', `Not counted per size (${n('not counted per size')})`], ['done', `Per size ✓ (${n('per size ✓')})`], ['recount', `Recount (${n('recount sizes')})`]] as const).map(([k, l]) => (
          <button key={k} className={cn('rounded-full border px-3 py-1 text-xs font-semibold', filter === k ? 'border-slate-800 bg-slate-800 text-white' : 'border-slate-300 bg-white text-slate-600')} onClick={() => setFilter(k)}>{l}</button>
        ))}
        <div className="relative ml-auto">
          <Search className="absolute left-2 top-2 h-4 w-4 text-slate-400" />
          <input className="rounded-lg border border-slate-300 bg-white py-1.5 pl-8 pr-3 text-sm outline-none" placeholder="Search a product…" value={q} onChange={(e) => setQ(e.target.value)} />
        </div>
      </div>

      <div className="space-y-2">
        {loading && !rows.length && <p className="p-6 text-center text-sm text-slate-400">Reading the products…</p>}
        {shown.map((r) => {
          const editing = open === r.groupId;
          const total = Object.values(counts).reduce((a, v) => a + (v === '' ? 0 : Number(v) || 0), 0);
          return (
            <div key={r.groupId} className="rounded-xl border border-slate-200 bg-white p-3">
              <div className="flex flex-wrap items-start gap-3">
                {r.imageUrl ? <img src={r.imageUrl} alt="" className="h-14 w-14 rounded-lg object-cover" /> : <div className="h-14 w-14 rounded-lg bg-slate-100" />}
                <div className="min-w-[220px] flex-1">
                  <p className="font-bold text-slate-800">{r.name}</p>
                  <div className="mt-0.5 flex flex-wrap items-center gap-2 text-xs text-slate-500">
                    <span><b className="text-slate-800">{r.stock}</b> in stock</span>
                    <span className={cn('rounded-full px-2 py-0.5 text-[11px] font-bold', STATUS_TONE[r.status] || 'bg-slate-100')}>{r.status}</span>
                    {r.suggestedName && <button className="inline-flex items-center gap-1 text-indigo-600 hover:underline" onClick={() => rename(r, r.suggestedName!)}><Pencil className="h-3 w-3" /> rename to “{r.suggestedName}”</button>}
                    {r.sameProductGroups.length > 0 && <span className="text-amber-700">· same product in {r.sameProductGroups.length} other group{r.sameProductGroups.length > 1 ? 's' : ''} (same name & cost)</span>}
                  </div>
                  <div className="mt-1.5 flex flex-wrap gap-1">
                    {r.labels.map((l) => (
                      <span key={l.label} className="rounded-lg border border-amber-300 bg-amber-50 px-2 py-0.5 text-xs font-bold text-amber-900" title={`${l.skus} SKU(s) of this size`}>
                        {l.label}{l.stock != null && <span className="ml-1 rounded bg-amber-900 px-1 text-white">{l.stock}</span>}<span className="ml-1 font-normal text-amber-700">{l.skus} sku</span>
                      </span>
                    ))}
                  </div>
                </div>
                <div className="flex flex-wrap gap-1.5">
                  <Button size="sm" onClick={() => openCount(r)}><Ruler className="h-3.5 w-3.5" /> Count per size</Button>
                  <Button size="sm" variant="outline" onClick={() => showSkus(r)}>{skusOf === r.groupId ? <ChevronDown className="h-3.5 w-3.5" /> : <ChevronRight className="h-3.5 w-3.5" />} SKUs</Button>
                  {r.sizes.length > 0 && <Button size="sm" variant="outline" onClick={() => clear(r)}><Undo2 className="h-3.5 w-3.5" /> One number</Button>}
                </div>
              </div>

              {editing && (
                <div className="mt-3 rounded-lg bg-slate-50 p-3">
                  <p className="mb-2 text-xs text-slate-600">Count what is on the shelf for each size. Leave empty a size we do not have. <b>Total = the new stock</b> of this product.</p>
                  <div className="flex flex-wrap items-end gap-2">
                    {Object.keys(counts).map((label) => (
                      <label key={label} className="flex w-20 flex-col text-xs font-bold text-slate-700">{label}
                        <input type="number" min={0} inputMode="numeric" className="mt-0.5 rounded-lg border border-slate-300 px-2 py-1.5 text-center text-base font-bold" value={counts[label]} onChange={(e) => setCounts({ ...counts, [label]: e.target.value })} />
                      </label>
                    ))}
                    <div className="flex items-end gap-1">
                      <input className="w-20 rounded-lg border border-slate-300 px-2 py-1.5 text-sm" placeholder="+ size" value={newSize} onChange={(e) => setNewSize(e.target.value.toUpperCase())} />
                      <Button size="sm" variant="outline" disabled={!newSize.trim()} onClick={() => { setCounts({ ...counts, [newSize.trim()]: '' }); setNewSize(''); }}>Add</Button>
                    </div>
                  </div>
                  <div className="mt-2 flex flex-wrap items-center gap-2 text-sm">
                    <span>Total <b>{total}</b> {total !== r.stock && <span className="text-amber-700">(stock now {r.stock})</span>}</span>
                    <Button size="sm" disabled={busy === r.groupId} onClick={() => saveCount(r)}><Check className="h-3.5 w-3.5" /> {busy === r.groupId ? 'Saving…' : 'Save count per size'}</Button>
                    <Button size="sm" variant="outline" onClick={() => setOpen(null)}><X className="h-3.5 w-3.5" /> Cancel</Button>
                  </div>
                </div>
              )}

              {skusOf === r.groupId && (
                <div className="mt-3 max-h-80 overflow-auto rounded-lg border border-slate-100">
                  <table className="w-full text-xs">
                    <thead className="sticky top-0 bg-slate-50 text-left text-slate-500"><tr><th className="p-1.5">SKU</th><th className="p-1.5">Store</th><th className="p-1.5">Title</th><th className="p-1.5">Size</th></tr></thead>
                    <tbody>
                      {!skus.length && <tr><td colSpan={4} className="p-3 text-center text-slate-400">Loading…</td></tr>}
                      {skus.map((s) => (
                        <tr key={s.id} className="border-t border-slate-100">
                          <td className="p-1.5 font-mono">{s.sku}</td><td className="p-1.5">{s.store}</td><td className="max-w-[320px] truncate p-1.5" title={s.name}>{s.name}</td>
                          <td className="p-1.5">
                            <input defaultValue={s.size || ''} placeholder="?" className={cn('w-20 rounded border px-1.5 py-0.5 font-bold uppercase', s.size ? 'border-amber-300 bg-amber-50' : 'border-red-300 bg-red-50')} onBlur={(e) => setSize(s, e.target.value)} />
                            {s.manual && <span className="ml-1 text-[10px] text-slate-400">set by hand</span>}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                  <p className="p-2 text-[11px] text-slate-400">A SKU without a size keeps the classic behaviour (the group's total). Type its size to include it.</p>
                </div>
              )}
            </div>
          );
        })}
        {!loading && !shown.length && <p className="p-6 text-center text-sm text-slate-400">Nothing here.</p>}
      </div>
    </div>
  );
};

export default SizesPage;
